/**
 * The user's track lists, as a fact about the page.
 *
 * `lib/symbol-index.ts`'s shape, for its reason: the screener grid, the group
 * bar, the symbol search dialog and the key handler all need to know which
 * symbols are in which list, and a copy per component is a set of copies that
 * can disagree. One of those disagreements is visible — a star filled in the
 * grid and hollow in the search results is the same bug the symbol index was
 * pulled out of `<SymbolSearch>` to fix.
 *
 * Module scope with subscribers rather than a context: publishing this through a
 * provider would put a re-render boundary around the whole shell, and the one
 * consumer that must **not** re-render on a change is the grid — it repaints one
 * column through `refreshCells` instead.
 *
 * Nothing here renders, and nothing imports React beyond the hook at the bottom.
 *
 * ## Writes are optimistic, and rolled back out loud
 *
 * A star must fill on the click, not a round trip later — the whole gesture is
 * meant to cost nothing. So the local state moves first and the request follows;
 * a failure restores the previous state and reports the server's own sentence.
 * The alternative, waiting, makes every add feel broken on a slow link; the
 * other alternative, ignoring failures, leaves a filled star over a symbol that
 * was never saved, which is worse than either.
 *
 * ## The membership index is derived, never stored twice
 *
 * `lists` is the truth; `bySymbol` is rebuilt from it on every change. Keeping
 * an index that is written alongside the lists is how the two stop agreeing —
 * and this one is read once per visible row per repaint, so it has to be a Map
 * rather than a scan of every list's members.
 */

import { useEffect, useReducer } from "react";

import {
  addToTrackList,
  createTrackList,
  deleteTrackList,
  fetchTrackLists,
  removeFromAllTrackLists,
  removeFromTrackList,
  reorderTrackLists,
  trackListsEnabled,
  updateTrackList,
  type TrackList,
  type UpdateListInput,
} from "../api/tracklists";

export type { TrackList };

/**
 * Palette slot → theme token.
 *
 * **The stored value is a slot name and the colour is resolved here**, which is
 * the app's one theming rule (D13): nothing holds a colour. A list keeps its
 * identity across a theme switch because what was stored is `violet`, not a hex
 * that is unreadable on White.
 *
 * The tokens are the chart's series tokens rather than new ones — the palette a
 * theme already has to define, so a list needs no contract change to be
 * themeable and a new theme gets list colours for free.
 */
export const COLOUR_TOKENS: Record<string, string> = {
  amber: "series.4",
  violet: "series.2",
  green: "positive",
  red: "negative",
  blue: "series.1",
  teal: "series.6",
  pink: "series.3",
  slate: "text.muted",
};

/** The default when a slot is unknown — a list still has to draw. */
const FALLBACK_TOKEN = "accent";

export function colourToken(slot: string | undefined): string {
  return (slot && COLOUR_TOKENS[slot]) || FALLBACK_TOKEN;
}

// ── the store ────────────────────────────────────────────────────────────────

let lists: readonly TrackList[] = [];
let bySymbol = new Map<string, TrackList[]>();
let loaded = false;
let loading: Promise<void> | null = null;
let lastError: string | null = null;
/**
 * Bumped on every change, and handed to the grid.
 *
 * The star column carries no data field, so AG Grid has no value it could
 * notice changing and would never repaint a cell on its own. This is the signal
 * — a number, so passing it cannot rebuild a column definition.
 */
let version = 0;

const listeners = new Set<() => void>();

function reindex(): void {
  const next = new Map<string, TrackList[]>();
  for (const list of lists) {
    for (const member of list.members) {
      const found = next.get(member.symbol);
      if (found) found.push(list);
      else next.set(member.symbol, [list]);
    }
  }
  bySymbol = next;
  version += 1;
  for (const notify of listeners) notify();
}

function setLists(next: readonly TrackList[]): void {
  // Sorted here, once, so every surface shows the same order: the group bar's
  // chips, the popover's rows and the manage screen cannot drift.
  lists = [...next].sort(
    (a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name),
  );
  reindex();
}

/** Replace one list in place, keeping the rest — what every write returns. */
function mergeList(updated: TrackList): void {
  const found = lists.some((list) => list.id === updated.id);
  setLists(found ? lists.map((l) => (l.id === updated.id ? updated : l)) : [...lists, updated]);
}

export function trackListsSnapshot(): readonly TrackList[] {
  return lists;
}

export function trackListsVersion(): number {
  return version;
}

export function trackListsError(): string | null {
  return lastError;
}

/** Which lists hold this symbol, in the user's order. Empty when none do. */
export function listsFor(symbol: string): readonly TrackList[] {
  return bySymbol.get(symbol) ?? [];
}

/** The alt-click target, or the first list when nothing wears the crown. */
export function defaultList(): TrackList | undefined {
  return lists.find((list) => list.isDefault) ?? lists[0];
}

/** The list bound to a digit, for the key handler. */
export function listForKey(key: string): TrackList | undefined {
  return lists.find((list) => list.hotKey === key);
}

/**
 * The lowest free digit, or null when all nine are taken.
 *
 * Null is not a failure: a list with no key still works through the menu, which
 * is why nine is a ceiling on *shortcuts* rather than on lists.
 */
export function nextFreeKey(): string | null {
  const taken = new Set(lists.map((list) => list.hotKey));
  for (const key of "123456789") if (!taken.has(key)) return key;
  return null;
}

/** `List 4` — the next name nobody has used, so a create needs no typing. */
export function nextFreeName(word = "List"): string {
  const taken = new Set(lists.map((list) => list.name.toLowerCase()));
  for (let n = 1; n <= 99; n += 1) {
    const candidate = `${word} ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return `${word} ${Date.now()}`;
}

/** The next unused palette slot, so two lists rarely share a colour. */
export function nextFreeColour(): string {
  const slots = Object.keys(COLOUR_TOKENS);
  const used = new Set(lists.map((list) => list.colour));
  return slots.find((slot) => !used.has(slot)) ?? slots[0]!;
}

/**
 * Load once per page.
 *
 * On mount rather than on demand, unlike the symbol index — this is a few
 * hundred bytes against that one's 2.7MB, and the star column cannot be drawn
 * at all until it lands. A *failure* is not cached: qv-data not being up yet is
 * the common case in development, and the next mount should retry.
 */
export function loadTrackLists(): Promise<void> {
  if (!trackListsEnabled()) return Promise.resolve();
  if (loaded) return Promise.resolve();
  if (!loading) {
    loading = fetchTrackLists()
      .then((catalog) => {
        loaded = true;
        lastError = null;
        setLists(catalog.lists);
      })
      .catch((error: unknown) => {
        loading = null;
        lastError = error instanceof Error ? error.message : String(error);
        // Notify anyway: a surface showing "lists unavailable" needs to hear
        // about the failure, and one showing nothing is unchanged by it.
        version += 1;
        for (const notify of listeners) notify();
      });
  }
  return loading;
}

// ── writes ───────────────────────────────────────────────────────────────────

/**
 * Apply a local change, run the request, and put it back if the request fails.
 *
 * The rollback captures `lists` **by reference before the change**, which is
 * safe because every mutation above replaces the array rather than editing it.
 * A rollback that rebuilt the previous state from the failure would be a second
 * implementation of the change, backwards.
 */
async function optimistic(
  apply: () => void,
  request: () => Promise<TrackList | void>,
): Promise<void> {
  const previous = lists;
  apply();
  try {
    const updated = await request();
    if (updated) mergeList(updated);
    lastError = null;
  } catch (error) {
    setLists(previous);
    lastError = error instanceof Error ? error.message : String(error);
    throw error;
  }
}

function withMembers(list: TrackList, symbols: readonly string[], closes?: Record<string, number>): TrackList {
  const present = new Set(list.members.map((m) => m.symbol));
  const stamp = new Date().toISOString();
  const added = symbols
    .filter((symbol) => !present.has(symbol))
    .map((symbol) => ({
      symbol,
      addedAt: stamp,
      addedClose: closes?.[symbol] ?? null,
    }));
  const members = [...list.members, ...added];
  return { ...list, members, memberCount: members.length };
}

function withoutMembers(list: TrackList, symbols: readonly string[]): TrackList {
  const drop = new Set(symbols);
  const members = list.members.filter((m) => !drop.has(m.symbol));
  return { ...list, members, memberCount: members.length };
}

export function addSymbols(
  listId: string,
  symbols: readonly string[],
  closes?: Record<string, number>,
): Promise<void> {
  const upper = symbols.map((s) => s.toUpperCase());
  return optimistic(
    () =>
      setLists(
        lists.map((l) => (l.id === listId ? withMembers(l, upper, closes) : l)),
      ),
    () => addToTrackList(listId, upper, closes),
  );
}

export function removeSymbols(listId: string, symbols: readonly string[]): Promise<void> {
  const upper = symbols.map((s) => s.toUpperCase());
  return optimistic(
    () => setLists(lists.map((l) => (l.id === listId ? withoutMembers(l, upper) : l))),
    () => removeFromTrackList(listId, upper),
  );
}

/**
 * Add the ones that are missing, or remove them all if every one is already in.
 *
 * The tri-state tick, decided **here** rather than in the menu, because the menu
 * renders the same answer in three places — the cell popover, the bulk popover
 * and the key handler — and three copies of "some are in" is three chances to
 * get the half state backwards.
 */
export function toggleSymbols(
  listId: string,
  symbols: readonly string[],
  closes?: Record<string, number>,
): Promise<void> {
  const list = lists.find((l) => l.id === listId);
  if (!list) return Promise.resolve();
  const present = new Set(list.members.map((m) => m.symbol));
  const upper = symbols.map((s) => s.toUpperCase());
  const allIn = upper.length > 0 && upper.every((symbol) => present.has(symbol));
  return allIn ? removeSymbols(listId, upper) : addSymbols(listId, upper, closes);
}

/** What a tick should draw for this set of symbols. */
export type TickState = "all" | "some" | "none";

export function tickState(list: TrackList, symbols: readonly string[]): TickState {
  if (symbols.length === 0) return "none";
  const present = new Set(list.members.map((m) => m.symbol));
  const hits = symbols.filter((symbol) => present.has(symbol.toUpperCase())).length;
  if (hits === 0) return "none";
  return hits === symbols.length ? "all" : "some";
}

export interface NewListSpec {
  id: string;
  name: string;
  hotKey?: string | null;
  colour?: string;
  isDefault?: boolean;
  symbols?: readonly string[];
  closes?: Record<string, number>;
}

export async function createList(spec: NewListSpec): Promise<TrackList> {
  const created = await createTrackList({
    id: spec.id,
    name: spec.name,
    hotKey: spec.hotKey ?? null,
    colour: spec.colour,
    isDefault: spec.isDefault,
    symbols: spec.symbols ? [...spec.symbols] : undefined,
    closes: spec.closes,
  });
  // **Not optimistic.** A create can be refused for a reason the client cannot
  // predict — a name taken in another tab — and a chip that appears and then
  // vanishes is worse than one that appears a beat late. The other writes are
  // toggles of a row that is already on screen; this one mints the row.
  //
  // The whole catalog is re-read rather than appended, because a create may have
  // moved the crown or taken a hot key off another list, and the response only
  // carries the new one.
  loaded = false;
  loading = null;
  await loadTrackLists();
  return created;
}

export async function renameList(id: string, patch: UpdateListInput): Promise<void> {
  const updated = await updateTrackList(id, patch);
  // A re-key or a new default touches a second list, so the catalog is re-read
  // for `createList`'s reason.
  if (patch.isDefault || patch.hotKey !== undefined || patch.clearHotKey) {
    loaded = false;
    loading = null;
    await loadTrackLists();
    return;
  }
  mergeList(updated);
}

export async function removeList(id: string): Promise<void> {
  const previous = lists;
  setLists(lists.filter((list) => list.id !== id));
  try {
    await deleteTrackList(id);
    // The crown may have passed to another list; only a re-read knows to which.
    loaded = false;
    loading = null;
    await loadTrackLists();
  } catch (error) {
    setLists(previous);
    lastError = error instanceof Error ? error.message : String(error);
    throw error;
  }
}

export async function reorderLists(ids: readonly string[]): Promise<void> {
  const previous = lists;
  const order = new Map(ids.map((id, index) => [id, index]));
  setLists(
    lists.map((list) => ({ ...list, displayOrder: order.get(list.id) ?? list.displayOrder })),
  );
  try {
    const { lists: fresh } = await reorderTrackLists([...ids]);
    setLists(fresh);
  } catch (error) {
    setLists(previous);
    lastError = error instanceof Error ? error.message : String(error);
    throw error;
  }
}

export async function removeFromAll(symbols: readonly string[]): Promise<void> {
  const previous = lists;
  const upper = symbols.map((s) => s.toUpperCase());
  setLists(lists.map((list) => withoutMembers(list, upper)));
  try {
    const { lists: fresh } = await removeFromAllTrackLists(upper);
    setLists(fresh);
  } catch (error) {
    setLists(previous);
    lastError = error instanceof Error ? error.message : String(error);
    throw error;
  }
}

// ── the hook ─────────────────────────────────────────────────────────────────

/**
 * Subscribe to the lists, loading them on first mount.
 *
 * A `useReducer` bump rather than `useSyncExternalStore`: the value is a module
 * variable read directly by the caller, so there is no snapshot to memoise and
 * nothing for the tearing guard to protect against.
 */
export function useTrackLists(): {
  lists: readonly TrackList[];
  version: number;
  error: string | null;
  enabled: boolean;
} {
  const [, bump] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    listeners.add(bump);
    void loadTrackLists();
    return () => {
      listeners.delete(bump);
    };
  }, []);

  return {
    lists,
    version,
    error: lastError,
    enabled: trackListsEnabled(),
  };
}
